import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { MapEntity } from "@mapmycustomers/shared/types/entity";
import { EntityPin } from "@mapmycustomers/shared/types/map";

import { EntityItemProperties } from "@app/component/RecordCard";
import EntityCard from "@app/component/RecordCard/components/EntityCard";
import EntityActions from "@app/scene/map/components/MapMode/components/EntityActions";
import {
  removeFromSelection,
  setPinHover,
  setSelectedPin,
} from "@app/scene/map/store/mapMode/actions";
import {
  getMapEntries,
  getMapViewTool,
  getPinHover,
  isRecordsListSortedByDistance,
} from "@app/scene/map/store/mapMode/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import { mapEntityIdGetter, mapPinIdGetter } from "@app/util/map/idGetters";

type EntityItemProps = {
  entityPin?: EntityPin;
  entityPins?: EntityPin[];
  pinHover?: string;
  showDistance: boolean;
  tool?: MapTool;
} & EntityItemProperties<MapEntity> &
  typeof mapDispatchToProps;

const EntityItem: React.FC<EntityItemProps> = ({
  entity,
  entityPin,
  fieldsComparator,
  onEmailSubscribe,
  onEmailUnsubscribe,
  onRemoveFromSelection,
  overrideFieldVisibility,
  pinHover,
  setPinHover,
  setSelectedPin,
  showDistance,
  showEntityView,
  tool,
}) => {
  const intl = useIntl();

  const isHighlighted = mapEntityIdGetter(entity) === pinHover;
  const isQuickActionVisible = isHighlighted && tool === MapTool.LASSO;

  const handleClick = useCallback(() => {
    setSelectedPin(entityPin);
    showEntityView({
      entityId: entity.id,
      entityType: entity.entity,
    });
  }, [entity, entityPin, showEntityView, setSelectedPin]);

  const handleHover = useCallback(() => {
    setPinHover(mapEntityIdGetter(entity));
  }, [entity, setPinHover]);

  const handleRemoveClick = useCallback(
    () => onRemoveFromSelection(mapEntityIdGetter(entity)),
    [entity, onRemoveFromSelection]
  );

  const handleLeave = useCallback(() => {
    setSelectedPin(undefined);
  }, [setSelectedPin]);

  const removeActionHoverText = useMemo(
    () =>
      intl.formatMessage({
        id: "map.recordList.mapMode.removeFromSelection",
        defaultMessage: "Remove from selection",
        description: "Remove from selection quick action title",
      }),
    [intl]
  );

  return (
    <EntityCard
      entity={entity}
      extraActions={<EntityActions />}
      fieldsComparator={fieldsComparator}
      isHighlighted={isHighlighted}
      isRemoveActionVisible={isQuickActionVisible}
      onClick={handleClick}
      onEmailSubscribe={onEmailSubscribe}
      onEmailUnsubscribe={onEmailUnsubscribe}
      onHover={handleHover}
      onLeave={handleLeave}
      onRemove={handleRemoveClick}
      overrideFieldVisibility={overrideFieldVisibility}
      removeActionHoverText={removeActionHoverText}
      showDistance={showDistance}
    />
  );
};

const mapStateToProps = (state: RootState, props: { entity: MapEntity }) => {
  const entityPins = getMapEntries(state).entities;
  const entityPin = entityPins?.find(
    (pin) => mapPinIdGetter(pin) === mapEntityIdGetter(props.entity)
  );

  return {
    entityPin,
    entityPins,
    pinHover: getPinHover(state),
    showDistance: isRecordsListSortedByDistance(state),
    tool: getMapViewTool(state),
  };
};

const mapDispatchToProps = {
  onRemoveFromSelection: removeFromSelection,
  setPinHover,
  setSelectedPin,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityItem);
